import { useEffect, useState } from "react";
import { editSourceUrl, probeVideoMetadata, resolveDeliveryUrl } from "../../components/vreEditSession";

/**
 * The video one batch tab edits. Normally the video's own source file; when
 * the card has raw clips picked (`rawVideoIds`, set by its RawVideoPicker),
 * that one clip, or several clips probed for a merged document — the same
 * choice the single editor makes. Raw clips exist only for WooGlobe WGIDs.
 *
 * Returns `{status: "loading" | "ready" | "error", videoUrl, videoSources, rawCount, notice}`.
 */
export default function useTabVideoSource({ item, rawVideos }) {
  const { video, wgid } = item;
  const ids = item.config?.rawVideoIds;
  const wantsRaw = Array.isArray(ids) && ids.length > 0 && !video?.is_compilation && !video?.is_native_video;
  const idsKey = wantsRaw ? ids.join(",") : "";
  const mainUrl = resolveDeliveryUrl(editSourceUrl(video), "s3");
  const [state, setState] = useState({ status: wantsRaw ? "loading" : "ready", videoUrl: mainUrl, videoSources: undefined, rawCount: 0, notice: "" });

  useEffect(() => {
    if (!idsKey) {
      setState({ status: "ready", videoUrl: mainUrl, videoSources: undefined, rawCount: 0, notice: "" });
      return undefined;
    }
    let live = true;
    const picked = idsKey.split(",");
    setState((s) => ({ ...s, status: "loading" }));
    (async () => {
      let clips = [];
      try {
        // Admin Portal's order, whatever order the clips were ticked in.
        clips = (await rawVideos(wgid)).filter((v) => picked.includes(String(v.id)));
      } catch (_) {
        clips = [];
      }
      if (!live) return;
      if (clips.length === 0) {
        setState({ status: "ready", videoUrl: mainUrl, videoSources: undefined, rawCount: 0, notice: "The raw clips picked on this card could not be loaded — editing the main video instead." });
        return;
      }
      const urls = clips.map((c) => resolveDeliveryUrl(c.url, "s3"));
      if (urls.length === 1) {
        setState({ status: "ready", videoUrl: urls[0], videoSources: undefined, rawCount: 1, notice: "" });
        return;
      }
      const probes = await Promise.all(urls.map(probeVideoMetadata));
      if (!live) return;
      const videoSources = urls.map((source, i) => ({ source, duration: probes[i].duration, width: probes[i].width, height: probes[i].height }));
      setState({
        status: probes.every((p) => p.ok) ? "ready" : "error",
        videoUrl: urls[0],
        videoSources,
        rawCount: urls.length,
        notice: "",
      });
    })();
    return () => {
      live = false;
    };
  }, [idsKey, wgid, mainUrl, rawVideos]);

  return state;
}
